import {
    ChangeDetectorRef,
    DestroyRef,
    Directive,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    inject
} from "@angular/core";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
    FormGroup,
    ControlContainer
} from "@angular/forms";
import {
    BehaviorSubject,
    combineLatestWith,
    map,
    distinctUntilChanged,
    Observable,
    startWith
} from "rxjs";
import {
    ObjectUtil
} from "@shopliftr/common-js/data";
import { DirectiveException } from "../exceptions/directive-exception";
import { COMMON_ERROR_HANDLER } from "../exceptions/constants/exception-constants";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { COMMON_APP_SERVICE } from "../application/constants/app-tokens";

/**
 * Adding this directive to a form adds some additional functionality.
 *
 * Specifically it will expose improved dirty form checking and a canSubmit flag which will
 * determine if a form is in a state in which it can be submitted.
 *
 * Template Driven EXAMPLE
 *
 * <form #supportRequestForm="ngForm" mgdEnhancedForm class="support-request" (ngSubmit)="onSubmit()">
 * ...
 * </form>
 *
 * Reactive EXAMPLE
 *
 * <form mgdEnhancedForm class="form" id="formGroup" [formGroup]="formGroup">
 * ...
 * </form>
 *
 * Browser autocomplete is suppressed by default. Set `allowBrowserAutocomplete` to
 * opt a form back in to the browser's saved-value suggestions.
 */
@Directive({
    selector: "form[mgdEnhancedForm]",
    exportAs: "mgdEnhancedForm",
    host: {
        "[attr.autocomplete]": "allowBrowserAutocomplete ? 'on' : 'off'"
    }
})
export class EnhancedFormDirective implements OnInit, OnChanges {

    /**
     * The formGroup for a reactive form;
     * This is an optional input that will only appear on a reactive form.
     *
     * For template driven form we use the formGroup from this._ngControl
     */
    // eslint-disable-next-line @shopliftr/angular/form-group-typed-generic -- accepts any consumer's FormGroup shape
    get formGroup(): FormGroup {

        // eslint-disable-next-line @shopliftr/angular/form-group-typed-generic -- see the getter's return type
        return this.reactiveFormGroup ?? this._ngControl.control as FormGroup;
    }

    /**
     * When this is true, the form will be disabled during processing
     *
     * Default is true
     */
    @Input() disableWhileProcessing?: boolean = true;

    /**
     * The formGroup for a reactive form
     *
     * @type {FormGroup}
     * @memberof EnhancedFormDirective
     */
    // eslint-disable-next-line @shopliftr/angular/form-group-typed-generic -- accepts any consumer's FormGroup shape
    @Input("formGroup") reactiveFormGroup: FormGroup;

    /**
     * Optional input to allow form submission even if no changes have been made.
     * Defaults to `false`. If `true`, the `isChanged` check is bypassed for `canSubmit`.
     * @type {boolean}
     */
    @Input({ transform: coerceBooleanProperty }) canSubmitWithoutChanges = false;

    /**
     * Optional input controlling whether the browser may offer its own saved-value
     * suggestions for the controls in this form.
     *
     * Defaults to `false`, which binds `autocomplete="off"` on the host `<form>`.
     * Set it to `true` to bind `autocomplete="on"` and let the browser suggest
     * previously entered values.
     *
     * @type {boolean}
     */
    @Input({ transform: coerceBooleanProperty }) allowBrowserAutocomplete = false;

    private readonly _isChangedSubject$: BehaviorSubject<boolean>;

    private readonly _isDisabledSubject$: BehaviorSubject<boolean>;

    private readonly _isValidSubject$: BehaviorSubject<boolean>;

    private readonly _canSubmitSubject$: BehaviorSubject<boolean>;

    private _unchangedValue: object;

    /**
     * Reference count of independent callers currently requesting that the form be disabled.
     * See {@link setDisabled} for the full pairing contract.
     */
    private _disableRequestCount = 0;

    /**
     * Whether this directive's own `disableWhileProcessing` handling currently holds an active
     * slot in `_disableRequestCount`. See {@link _setProcessingDisabled} for why this exists.
     */
    private _processingDisableActive = false;

    private readonly _ngControl = inject(ControlContainer);

    private readonly _errorHandler = inject(COMMON_ERROR_HANDLER);

    private readonly _destroyRef = inject(DestroyRef);

    private readonly _appState = inject(COMMON_APP_SERVICE);

    private readonly _cdRef = inject(ChangeDetectorRef);

    constructor() {

        this._isChangedSubject$ = new BehaviorSubject(false);
        this._isValidSubject$ = new BehaviorSubject(true);
        this._isDisabledSubject$ = new BehaviorSubject(false);
        this._canSubmitSubject$ = new BehaviorSubject(false);

        // Update canSubmit$ whenever isChanged$, isDisabled$, or isValid$ changes
        this.isChanged$.pipe(
            combineLatestWith(this.isDisabled$, this.isValid$),
            map(([changed, disabled, valid]) => (changed || this.canSubmitWithoutChanges) && !disabled && valid),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(this._canSubmitSubject$);
    }

    /**
     * Returns true if this is a reactive from with a proper formGroup input defined.
     * If false its is a template driven from
     */
    get reactiveForm(): boolean {

        return Boolean(this.reactiveFormGroup);
    }

    /**
     * Emits true if the value of the form has changed from its initial unchanged state,
     * and only emits when the changed state changes
     */
    get isChanged$(): Observable<boolean> {

        return this._isChangedSubject$.asObservable().pipe(
            distinctUntilChanged()
        );
    }

    /**
     * Returns true if the value of the form has changed from its initial state
     */
    get isChanged(): boolean {

        return this._isChangedSubject$.value;
    }

    /**
     * Emits true if the form is disabled and emit only when the disabled state changes
     *
     */
    get isDisabled$(): Observable<boolean> {

        return this._isDisabledSubject$.asObservable().pipe(
            distinctUntilChanged()
        );
    }

    /**
     * Returns true if the form is disabled
     */
    get isDisabled(): boolean {

        return this._isDisabledSubject$.value;
    }

    /**
     * Emits true if the form has no validation errors
     * and only emits when the valid state changes
     */
    get isValid$(): Observable<boolean> {

        return this._isValidSubject$.asObservable().pipe(
            distinctUntilChanged()
        );
    }

    /**
     * Returns true if the form has no validation errors
     */
    get isValid(): boolean {

        return this._isValidSubject$.value;
    }

    /**
     * Emits true if the form can be submitted.
     * It is dirty, it has no validation errors, it is not disabled.
     * Emit only when the can submit state changes
     */
    get canSubmit$(): Observable<boolean> {

        return this._canSubmitSubject$.asObservable().pipe(
            distinctUntilChanged()
        );
    }

    /**
     * Returns true if the form can be submitted.
     * It is dirty, it has no validation errors, it is not disabled.
     */
    get canSubmit(): boolean {

        return (this.isChanged || this.canSubmitWithoutChanges) && !this.isDisabled && this.isValid;
    }

    /**
     * Angular OnChanges lifecycle event
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (changes.disableWhileProcessing) {

            if (this.disableWhileProcessing) {
                this._setProcessingDisabled(this._appState.processing);
            }
            else {
                this._setProcessingDisabled(false);
            }
            this._cdRef.markForCheck();
        }
        if (changes.canSubmitWithoutChanges) {
            this._canSubmitSubject$.next(changes.canSubmitWithoutChanges.currentValue as boolean);
        }
    }

    /**
     * Angular OnInit lifecycle event
     */
    ngOnInit(): void {

        // Set initial state for the form, including disabled control values
        this._unchangedValue = this.formGroup.getRawValue();
        // Set the initial disabled state for the form
        this._isDisabledSubject$.next(this.formGroup.disabled);

        // This subscription will update the $isChanged observable whenever the formGroup value changes
        this._ngControl.valueChanges.pipe(
            // Every time the value of the form group changes compare it to the initial value for the form group.
            // We use _getControlValues() instead of the emitted currentValue to always include disabled controls.
            map(() => {
                const currentValue = this.formGroup.getRawValue() as object;
                if (!this.reactiveForm && Object.keys(this._unchangedValue).length !== Object.keys(currentValue).length) {
                    // This handles the template driven form cases where formControls are added well after the ngAfterViewInit
                    // event has fired
                    this._unchangedValue = currentValue;
                    return false;
                }
                const changes = ObjectUtil.getChanges(this._unchangedValue, currentValue);
                return changes.hasChanges;
            }),
            takeUntilDestroyed(this._destroyRef)
        )
            .subscribe(this._isChangedSubject$);

        this.formGroup.statusChanges.pipe(
            // Ensure that when we're setting up the subscription it returns the formGroup valid state
            startWith(undefined),
            // Return the valid state when ever the form status changes
            map(() => this.formGroup.valid),
            takeUntilDestroyed(this._destroyRef)
        )
            .subscribe(this._isValidSubject$);

        this.formGroup.statusChanges.pipe(
            // Ensure that when we're setting up the subscription it returns the formGroup disabled state
            startWith(undefined),
            // Return the disabled state when ever the form status changes
            map(() => this.formGroup.disabled),
            takeUntilDestroyed(this._destroyRef)
        )
            .subscribe(this._isDisabledSubject$);

        this._appState.processing$.pipe(
            takeUntilDestroyed(this._destroyRef)
        )
            .subscribe((processing) => {
                if (this.disableWhileProcessing) {
                    this._setProcessingDisabled(processing);
                }
                else {
                    this._setProcessingDisabled(false);
                }
                this._cdRef.markForCheck();
            });
    }

    /**
     * Reset the form to its unchangedState
     */
    reset(): void {

        this.formGroup?.reset(this._unchangedValue);
    }

    /**
     * Manually set the unchanged state to the current state of the form
     *
     * The form will no longer be marked changed after a new unchanged state is set
     */
    snapshotUnchangedState(): void {

        this._unchangedValue = this.formGroup.getRawValue();

        this._isChangedSubject$.next(false);
    }

    /**
     * The snapshotted unchanged-state raw value at a control path, e.g. `localizations.fr` —
     * whatever `path` resolved to the last time `snapshotUnchangedState()` ran (including the
     * one every `populate()` call performs by default). Returns `undefined` if no snapshot has
     * been taken yet, rather than throwing — a consumer that reads this before `ngOnInit` has
     * run (e.g. a sibling component's own first render pass, which can happen before this
     * directive's `ngOnInit` if it appears later in the same template) gets a harmless
     * "no snapshot yet" answer instead of a hard failure.
     *
     * @param path - A `formGroup.get()`-style path, dot-separated for a nested control
     */
    snapshotValueAt(path: string): unknown {

        return this._unchangedValue ? ObjectUtil.getValue(path, this._unchangedValue) : undefined;
    }

    /**
     * Whether the control at `path` differs in value from the snapshot. For a path resolving to
     * a `FormGroup`/`FormRecord`, this runs through the same `ObjectUtil.getChanges` comparison
     * {@link isChanged} itself uses, so a consumer built on this (e.g. a per-tab indicator) can
     * never disagree with `canSubmit` about whether something actually changed. `getChanges`
     * only accepts objects, so a path resolving to a bare `FormControl` instead falls back to a
     * plain inequality check on its two non-object values (arrays are handled separately, below) — there is no "fields" to diff for a
     * single value, only whether it changed.
     *
     * Compares against the control's own `getRawValue()` where one exists, never `.value`:
     * `FormGroup.value` omits disabled child controls, so a `.value`-based comparison would see
     * every disabled group as having gone empty rather than as unchanged — the exact situation a
     * Localized Domain Form is in for every non-selected language's group.
     *
     * A current value and a snapshot value that are both nullish (`null` or `undefined`) are unchanged,
     * since the snapshot lookup cannot distinguish the two.
     *
     * The snapshot side gets the same "is this actually an object" guard as the current-value
     * side, but with the opposite conclusion when it fails: a path can resolve to `null`/`undefined`
     * at snapshot time even though the live control is a `FormGroup` (e.g. an image sub-group on a
     * freshly-created record that had no image yet). If the snapshot had nothing there and the
     * control now holds a real object, that is unambiguously a change — there is nothing to diff,
     * only something that now exists where it previously didn't — so this short-circuits to `true`
     * rather than handing a `null`/`undefined` `original` to `ObjectUtil.getChanges`, which requires
     * a real object on both sides and throws (`Object.keys` on `null`) otherwise.
     *
     * Arrays are compared as a whole through `ObjectUtil.equals`, since `ObjectUtil.getChanges`
     * throws for them: the comparison is deep and order-sensitive, so a reorder counts as a change,
     * while object elements and nested arrays compare by value. An array against a non-array,
     * including a `null` snapshot against an empty array, is a change.
     *
     * @param path - A `formGroup.get()`-style path, dot-separated for a nested control
     */
    isChangedAt(path: string): boolean {

        const control = this.formGroup?.get(path);

        if (!control || !this._unchangedValue) {
            return false;
        }

        // eslint-disable-next-line @shopliftr/angular/form-group-typed-generic -- accepts any consumer's shape
        const group = control as FormGroup;
        const currentValue = typeof group.getRawValue === "function" ? group.getRawValue() : control.value;
        const snapshotValue = this.snapshotValueAt(path);

        // `null` and `undefined` both mean "no value": `snapshotValueAt` reads a `null` as `undefined`,
        // so a field populated with `null` must not read as changed against its own snapshot.
        if ((currentValue === null || currentValue === undefined) && (snapshotValue === null || snapshotValue === undefined)) {
            return false;
        }

        if (Array.isArray(currentValue) || Array.isArray(snapshotValue)) {
            return !ObjectUtil.equals(snapshotValue, currentValue);
        }

        if (typeof currentValue !== "object" || currentValue === null) {
            return currentValue !== snapshotValue;
        }

        if (typeof snapshotValue !== "object" || snapshotValue === null) {
            return true;
        }

        return ObjectUtil.getChanges(snapshotValue as object, currentValue as object).hasChanges;
    }

    /**
     * Set the disabled state of the form.
     *
     * Disabling is reference-counted rather than a simple boolean. Multiple independent callers
     * (e.g. several open `<mgd-subform>` instances, plus this directive's own
     * `disableWhileProcessing` handling) may each call `setDisabled(true)` on the same directive
     * instance at overlapping times. The underlying `formGroup` is only actually disabled when the
     * first blocker calls `setDisabled(true)`, and only re-enabled once every blocker has released
     * its request via a matching `setDisabled(false)` - so one caller finishing (e.g. cancelling one
     * subform) never re-enables the form while another caller (e.g. a sibling subform) still needs
     * it disabled. The count is clamped at a floor of 0, so a stray `setDisabled(false)` with no
     * matching prior `setDisabled(true)` is a safe no-op rather than going negative.
     *
     * Because `formGroup.disable()`/`.enable()` are only invoked at the 0→1 and 1→0 count
     * transitions - never on repeated calls that leave the count on the same side of that
     * threshold - a caller that calls `setDisabled(true)` (or `setDisabled(false)`) more than once
     * in a row never re-triggers `AbstractControl.disable()`/`.enable()`. This matters because
     * `disable()`/`.enable()` unconditionally emit `valueChanges` (and `statusChanges`) for the
     * group and every child control, which would otherwise reset any `debounceTime` pipeline
     * downstream of a control's `valueChanges` (e.g. a debounced keyword filter) on every redundant
     * call.
     *
     * Note that this reference count is this directive's own bookkeeping, not a read of the live
     * `formGroup.disabled` getter - Angular recomputes that getter from the aggregate state of all
     * child controls, so it can change independently of calls made through this method (e.g. if
     * something else in the form tree enables one child control directly). Trusting the count
     * instead of the live getter is what keeps the 0→1/1→0 transition detection correct
     * regardless of what else touches the form's children. See {@link _setProcessingDisabled} for
     * how this directive's own `disableWhileProcessing` blocker participates in the same count.
     */
    setDisabled(value: boolean): void {

        if (value) {
            this._disableRequestCount++;
            if (this._disableRequestCount === 1) {
                this.formGroup?.disable();
            }
        }
        else {
            this._disableRequestCount = Math.max(0, this._disableRequestCount - 1);
            if (this._disableRequestCount === 0) {
                this.formGroup?.enable();
            }
        }
    }

    /**
     * Only supported for reactive forms. Sets form values for the formGroup
     * given a map of formGroup keys and their values.
     * Each form control will be marked as touched and updated.
     * After all values are set, the form will be marked as unchanged unless skipSnapshot is true.
     *
     * An empty map is valid: no control values are written, and the form is simply
     * marked as unchanged. Use this after resetting a form to its default values.
     * @param formValues Map of formGroup keys and their values
     * @param skipSnapshot If true, the form will not be marked as unchanged after populating values
     * @throws {DirectiveException} if the formGroup is not reactive
     * @throws {DirectiveException} if the formValues map is null
     * @throws {DirectiveException} if the formGroup does not contain a key in the map
     */
    populate(formValues: Map<string, any>, skipSnapshot: boolean = false): void {
        try {
            if (!this.reactiveForm) {
                throw new DirectiveException(`mgdEnhancedForm populate: function not supported for template driven form`);
            }
            if (!formValues) {
                throw new DirectiveException(`mgdEnhancedForm populate: formValues is null`);
            }

            for (const [key, value] of formValues) {
                if (!(this.formGroup?.get(key))) {
                    throw new DirectiveException(`mgdEnhancedForm populate: Key ${key} not found in formGroup`);
                }
                else {
                    this.formGroup.get(key)?.setValue(value, { emitEvent: false });
                    this.formGroup.get(key)?.markAsTouched();
                    this.formGroup.get(key)?.updateValueAndValidity();
                }
            }
            if (!skipSnapshot) {
                this.snapshotUnchangedState();
            }
        }
        catch (error) {
            this._errorHandler.handleDirectiveError(error);
            throw error;
        }
    }

    /**
     * Acquires or releases this directive's own "disable while processing" blocker slot in
     * `_disableRequestCount` (see {@link setDisabled} for the reference-counting contract).
     *
     * `ngOnChanges` (on `disableWhileProcessing` input changes) and the `processing$` subscription
     * in `ngOnInit` both represent the same logical blocker, not two independent ones. Routing both
     * call sites through this method - for BOTH acquiring and releasing - ensures this directive
     * only ever holds at most one "processing" slot in the shared counter, and that releasing it
     * only ever gives up that one slot rather than disturbing any other outstanding blocker (e.g.
     * an open `<mgd-subform>`'s own `setDisabled(true)`).
     *
     * Callers pass `false` in two situations that both mean "this directive's processing blocker
     * should not be held right now": a `processing$` emission of `false`, and `disableWhileProcessing`
     * being turned off entirely (in which case the caller passes `false` unconditionally, regardless
     * of the current `processing` value - see the `ngOnChanges` and `processing$` subscription call
     * sites). In both cases the release is driven by `_processingDisableActive`, this directive's own
     * record of whether it previously acquired the slot - never by reading `formGroup.disabled`.
     * Angular recomputes that getter from the live aggregate state of every child control, so it can
     * flip independently of anything this directive did (e.g. another part of the form tree
     * re-enabling one child control), making it an unreliable signal for "did *this directive*
     * disable the form". Tracking intent explicitly instead means: if this directive never acquired
     * the slot, releasing is a safe no-op that leaves other blockers (and `_disableRequestCount`)
     * completely untouched - unlike a blanket reset of the whole counter, which would silently drop
     * a subform's outstanding blocker any time a `processing$` tick fires while `disableWhileProcessing`
     * is `false`.
     *
     * @param shouldDisable Whether the "disable while processing" blocker should currently be held.
     */
    private _setProcessingDisabled(shouldDisable: boolean): void {

        if (shouldDisable && !this._processingDisableActive) {
            this._processingDisableActive = true;
            this.setDisabled(true);
        }
        else if (!shouldDisable && this._processingDisableActive) {
            this._processingDisableActive = false;
            this.setDisabled(false);
        }
    }

}
